import {
  getRedirectResult,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth';
import { createContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { auth } from '../../shared/firebase/client';

export interface AuthState {
  user: User | null;
  /** ยังไม่รู้ว่า login อยู่หรือเปล่า — ต้องรอก่อนตัดสินใจ redirect */
  isLoading: boolean;
  /** error ที่เกิดตอนกลับมาจาก redirect sign-in — ถ้าไม่เก็บไว้จะเงียบหายไปเลย */
  redirectError: unknown;
  clearRedirectError: () => void;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [redirectError, setRedirectError] = useState<unknown>(null);

  useEffect(() => {
    // onAuthStateChanged ยิงครั้งแรกเสมอหลัง SDK อ่าน session จาก storage เสร็จ
    // ก่อนหน้านั้นห้ามตัดสินว่า "ไม่ได้ login" ไม่งั้นจะเด้งไป /login ทุกครั้งที่ refresh
    return onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    // เก็บผลของ signInWithRedirect ที่เพิ่งกลับมา — ตัว session จัดการโดย onAuthStateChanged อยู่แล้ว
    // ที่ต้องดักคือ "error" ซึ่งไม่มีใครรับช่วงต่อ ถ้าไม่จับตรงนี้ผู้ใช้จะเห็นแค่หน้า login เปล่า ๆ
    // โดยไม่รู้ว่าเกิดอะไรขึ้น
    getRedirectResult(auth).catch((caught: unknown) => {
      setRedirectError(caught);
    });
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      isLoading,
      redirectError,
      clearRedirectError: () => setRedirectError(null),
      signOut: () => firebaseSignOut(auth),
    }),
    [user, isLoading, redirectError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
